import React, { memo } from 'react';
import { Box, ButtonBase, IconButton, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import { MoreVert, StarRounded } from '@mui/icons-material';
import type { IdCardDesign } from '../../../types/idCards';
import { ToneChip } from '../../common/StatusChip';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import IdCardPreview from '../IdCardPreview';
import { layoutOf } from '../catalog';
import type { CardPerson } from '../cardPeople';
import type { CardSchool } from '../render/faces/shared';

interface Props {
  design: IdCardDesign;
  school: CardSchool;
  person: CardPerson;
  photo: string | null;
  validUntil: string | null;
  onOpen: (design: IdCardDesign) => void;
  /** Absent when the user may not change designs. */
  onMenu?: (anchor: HTMLElement, design: IdCardDesign) => void;
}

/** A design in the gallery: its front with the back fanned out behind it. */
const DesignTile: React.FC<Props> = ({ design, school, person, photo, validUntil, onOpen, onMenu }) => {
  const theme = useTheme();
  const layout = layoutOf(design.layout);
  const portrait = layout.orientation === 'portrait';
  const face = { design, person, photo, school, validUntil };
  const width = portrait ? 100 : 206;
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3, overflow: 'hidden', position: 'relative',
        transition: 'box-shadow .2s, transform .2s, border-color .2s',
        '&:hover': { boxShadow: `0 12px 28px -12px ${alpha('#0f172a', 0.28)}`, transform: 'translateY(-2px)', borderColor: alpha(theme.palette.primary.main, 0.35) },
      }}
    >
      <ButtonBase onClick={() => onOpen(design)} sx={{ display: 'block', width: '100%', textAlign: 'left' }} aria-label={`Open ${design.name}`}>
        <Box
          sx={{
            height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
            background: `linear-gradient(160deg, ${alpha(design.colors.primary, 0.1)}, ${alpha(design.colors.accent, 0.08)})`,
          }}
        >
          <Box sx={{ position: 'relative', mr: design.back.enabled ? 3 : 0, mt: design.back.enabled ? -2.5 : 0 }}>
            {design.back.enabled && (
              <Box sx={{ position: 'absolute', left: 24, top: 18, transform: 'rotate(4deg)', opacity: 0.95 }}>
                <IdCardPreview {...face} side="back" width={width} flat />
              </Box>
            )}
            <Box sx={{ position: 'relative', transform: 'rotate(-2deg)' }}>
              <IdCardPreview {...face} side="front" width={width} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ px: 2, pt: 1.5, pb: 1.75 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: onMenu ? 4 : 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{design.name}</Typography>
            {design.isDefault && <ToneChip label="Default" tone="highlight" icon={StarRounded} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {layout.label} · {design.back.enabled ? 'Two-sided' : 'Front only'} · Edited {dateTimeUtils.formatDateForDisplay(design.updatedAt)}
          </Typography>
        </Box>
      </ButtonBase>
      {onMenu && (
        <IconButton size="small" onClick={e => onMenu(e.currentTarget, design)} aria-label={`Actions for ${design.name}`}
          sx={{ position: 'absolute', right: 8, bottom: 22 }}>
          <MoreVert fontSize="small" />
        </IconButton>
      )}
    </Paper>
  );
};

export default memo(DesignTile);
